// Merkan · gemeinsame Oberflächenbausteine (GR-06, GR-11)
import { createContext, useCallback, useContext, useEffect, useRef, useState, type DependencyList, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronRight, Info } from 'lucide-react';
import { IcAngenommen as CheckCircle2, IcPosteingang as Inbox, IcSuche as Search, IcSchliessen as X, IcAbgelehnt as XCircle } from './icons';
import { euro, euroCent } from '../lib/format';
import { IcAbgelehnt, IcAngenommen, IcEntwurf, IcGesendet, IcInBearbeitung, IcUeberfaellig } from './icons';
import { t, gebiet } from '../lib/sprache';

// ---------------------------------------------------------------------------
// Seitenkopf mit Pfad
// ---------------------------------------------------------------------------
export interface PfadTeil { name: string; zu?: string }

export function Seitenkopf({ pfad, titel, untertitel, aktionen }: { pfad: PfadTeil[]; titel: ReactNode; untertitel?: ReactNode; aktionen?: ReactNode }) {
  useEffect(() => {
    if (typeof titel === 'string') document.title = `${titel} · Merkan`;
  }, [titel]);
  return (
    <header className="seitenkopf">
      <div style={{ minWidth: 0 }}>
        <nav className="pfad" aria-label={t('Pfad')}>
          {pfad.map((p, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {i > 0 && <ChevronRight size={12} className="sep" />}
              {p.zu ? <Link to={p.zu}>{p.name}</Link> : <span>{p.name}</span>}
            </span>
          ))}
        </nav>
        <h1>{titel}</h1>
        {untertitel && <div className="untertitel">{untertitel}</div>}
      </div>
      {aktionen && <div className="aktionen">{aktionen}</div>}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Knopf
// ---------------------------------------------------------------------------
type KnopfArt = 'primaer' | 'gefahr' | 'leise' | undefined;
/** Button (DS-06): Varianten primär, sekundär (Standard), leise, Gefahr; Größen Standard 36 px und klein 28 px.
 *  Druck scale(0.97) nur bei Zeigerbedienung, Hover nur bei feinem Zeiger, Fokusring 2 px in focus. */
export function Knopf({ art, klein, icon, children, className, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement> & { art?: KnopfArt; klein?: boolean; icon?: ReactNode }) {
  const kl = ['kn', art ? `kn-${art}` : '', klein ? 'kn-klein' : '', !children && icon ? 'kn-icon' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <button type="button" className={kl} {...rest}>
      {icon}
      {children}
    </button>
  );
}

export type SpeichernZustand = 'bereit' | 'speichert' | 'gespeichert';
/** Speichern-Button mit fester Breite: Zustände Bereit, Speichert, Gespeichert, Überblendung mit 2 px Unschärfe (DS-06) */
export function SpeichernKnopf({ zustand, texte, className, ...rest }: Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  zustand: SpeichernZustand; texte?: Partial<Record<SpeichernZustand, string>>;
}) {
  const tx: Record<SpeichernZustand, string> = { bereit: t('Speichern'), speichert: t('Speichert …'), gespeichert: t('Gespeichert'), ...texte };
  return (
    <button type="button" className={`kn kn-primaer kn-speichern${className ? ` ${className}` : ''}`} aria-live="polite" disabled={zustand === 'speichert' || rest.disabled} {...rest}>
      {(Object.keys(tx) as SpeichernZustand[]).map((z) => (
        <span key={z} className="kn-speichern-text" data-aktiv={z === zustand} aria-hidden={z !== zustand}>
          {z === 'gespeichert' && <IcAngenommen size={16} />}{tx[z]}
        </span>
      ))}
    </button>
  );
}

/** Halten zum Bestätigen, z. B. Storno ohne Dialog: Füllung per clip-path linear in 1,6 s, Loslassen 200 ms zurück.
 *  Tastatur: Leertaste oder Enter gedrückt halten. Bei reduzierter Bewegung bleibt die Haltezeit, die Füllung springt. */
export function HaltenKnopf({ children, icon, bestaetigt, dauer = 1600, disabled, className, ...rest }: Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
  icon?: ReactNode; bestaetigt: () => void; dauer?: number;
}) {
  const [haelt, setHaelt] = useState(false);
  const zeit = useRef<number | null>(null);
  const start = () => {
    if (disabled || zeit.current != null) return;
    setHaelt(true);
    zeit.current = window.setTimeout(() => { zeit.current = null; setHaelt(false); bestaetigt(); }, dauer);
  };
  const ende = () => { if (zeit.current != null) { window.clearTimeout(zeit.current); zeit.current = null; } setHaelt(false); };
  useEffect(() => ende, []);
  return (
    <button
      type="button" className={`kn kn-gefahr kn-halten${className ? ` ${className}` : ''}`} data-haelt={haelt} disabled={disabled}
      style={{ ['--halten-dauer' as string]: `${dauer}ms` }}
      onPointerDown={start} onPointerUp={ende} onPointerLeave={ende} onPointerCancel={ende}
      onKeyDown={(e) => { if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) { e.preventDefault(); start(); } }}
      onKeyUp={(e) => { if (e.key === ' ' || e.key === 'Enter') ende(); }}
      {...rest}
    >
      <span className="kn-halten-fuellung" aria-hidden="true" />
      {icon}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Marke, Hinweis, Leer, Laden
// ---------------------------------------------------------------------------
export type MarkenArt = 'gut' | 'warn' | 'fehler' | 'akzent' | 'info' | 'neutral';
// StatusBadge (DS-06): immer Icon 14 px plus Wort. Varianten neutral, info, success, warning, danger, accent.
const MARKEN_ICON: Record<MarkenArt, (p: { size?: number }) => JSX.Element> = {
  neutral: IcEntwurf, info: IcGesendet, gut: IcAngenommen, warn: IcUeberfaellig, fehler: IcAbgelehnt, akzent: IcInBearbeitung,
};
/** Status als Badge. `icon` ersetzt das Standard-Icon der Variante; `punkt` bleibt aus Kompatibilität erhalten und hat keine Wirkung mehr. */
export function Marke({ art = 'neutral', icon, children, title }: { art?: MarkenArt; punkt?: boolean; icon?: ReactNode; children: ReactNode; title?: string }) {
  const Icon = MARKEN_ICON[art];
  return (
    <span className={`marke${art !== 'neutral' ? ` marke-${art}` : ''}`} title={title}>
      {icon ?? <Icon size={14} />}
      {children}
    </span>
  );
}

export function Hinweis({ art = 'info', children }: { art?: 'info' | 'warn' | 'fehler' | 'gut'; children: ReactNode }) {
  const icon = art === 'warn' ? <AlertTriangle /> : art === 'fehler' ? <XCircle /> : art === 'gut' ? <CheckCircle2 /> : <Info />;
  return (
    <div className={`hinweis${art !== 'info' ? ` hinweis-${art}` : ''}`} role={art === 'fehler' ? 'alert' : undefined}>
      {icon}
      <div>{children}</div>
    </div>
  );
}

export function Leer({ icon, titel, text, aktion }: { icon?: ReactNode; titel: string; text?: ReactNode; aktion?: ReactNode }) {
  return (
    <div className="leer">
      {icon ?? <Inbox />}
      <div className="titel">{titel}</div>
      {text && <div className="klein">{text}</div>}
      {aktion}
    </div>
  );
}

export function LadeZeilen({ anzahl = 5 }: { anzahl?: number }) {
  return (
    <div style={{ display: 'grid', gap: 12, padding: 16 }}>
      {Array.from({ length: anzahl }).map((_, i) => (
        <div key={i} className="platzhalter-zeile" style={{ width: `${90 - (i % 3) * 15}%` }} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Beträge (Präsentationsmodus blendet .betrag aus)
// ---------------------------------------------------------------------------
export function Cent({ wert, animiert }: { wert: number | null | undefined; animiert?: boolean }) {
  if (animiert && wert != null) return <span className="betrag zahl"><ZahlAnimiert wert={Number(wert)} format={(n) => euroCent(n)} /></span>;
  return <span className="betrag zahl">{euroCent(wert)}</span>;
}
export function Euro({ wert, animiert }: { wert: number | string | null | undefined; animiert?: boolean }) {
  if (animiert && wert != null && !Number.isNaN(Number(wert))) return <span className="betrag zahl"><ZahlAnimiert wert={Number(wert)} format={(n) => euro(n)} /></span>;
  return <span className="betrag zahl">{euro(wert)}</span>;
}

// ---------------------------------------------------------------------------
// Bewegung beim Erscheinen: Zahlen zählen hoch, Balken wachsen (nur einmal, sobald sichtbar)
// ---------------------------------------------------------------------------
const wenigBewegung = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Liefert einen Ref und ob das Element schon einmal sichtbar war */
export function useImBild<T extends Element = HTMLDivElement>(): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [sichtbar, setSichtbar] = useState(() => wenigBewegung());
  useEffect(() => {
    if (sichtbar || !ref.current || typeof IntersectionObserver === 'undefined') { if (!sichtbar) setSichtbar(true); return; }
    const io = new IntersectionObserver((e) => { if (e.some((x) => x.isIntersecting)) { setSichtbar(true); io.disconnect(); } }, { rootMargin: '0px 0px -40px 0px' });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [sichtbar]);
  return [ref, sichtbar];
}

const ganzzahl = new Intl.NumberFormat(gebiet());
/** Zählt beim ersten Erscheinen von 0 auf den Wert hoch (ca. 0,9 s, stark abbremsend); spätere Änderungen gleiten vom alten Wert */
export function ZahlAnimiert({ wert, format = (n) => ganzzahl.format(Math.round(n)), dauer = 900 }: { wert: number; format?: (n: number) => string; dauer?: number }) {
  const [ref, sichtbar] = useImBild<HTMLSpanElement>();
  const [anzeige, setAnzeige] = useState(() => (wenigBewegung() ? wert : 0));
  const zuletzt = useRef<number | null>(null);
  useEffect(() => {
    if (!sichtbar) return;
    if (wenigBewegung()) { setAnzeige(wert); return; }
    const von = zuletzt.current ?? 0;
    zuletzt.current = wert;
    const start = performance.now();
    let id = 0;
    const schritt = (t: number) => {
      const x = Math.min(1, (t - start) / dauer);
      const e = 1 - Math.pow(1 - x, 4);
      setAnzeige(von + (wert - von) * e);
      if (x < 1) id = requestAnimationFrame(schritt);
    };
    id = requestAnimationFrame(schritt);
    return () => cancelAnimationFrame(id);
  }, [sichtbar, wert, dauer]);
  const ganz = Number.isInteger(wert);
  return <span ref={ref} className="zahl-animiert">{format(ganz ? Math.round(anzeige) : anzeige)}</span>;
}

/** Platzhalter für einen Wert, solange er lädt (verschwommener Schimmer) */
export function Platzhalter({ breite = '4em', hoehe = '1em' }: { breite?: string; hoehe?: string }) {
  return <span className="platzhalter-wert" style={{ width: breite, height: hoehe }} aria-hidden="true" />;
}

/** Platzhalter für Kennzahl-Kacheln */
export function LadeKacheln({ anzahl = 4 }: { anzahl?: number }) {
  return (
    <>
      {Array.from({ length: anzahl }).map((_, i) => (
        <div key={i} className="kachel kachel-platzhalter" aria-hidden="true">
          <span className="platzhalter-zeile" style={{ width: '45%', height: 12 }} />
          <span className="platzhalter-zeile" style={{ width: '65%', height: 28, marginTop: 10 }} />
          <span className="platzhalter-zeile" style={{ width: '80%', height: 10, marginTop: 12 }} />
        </div>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Eingaben
// ---------------------------------------------------------------------------
export function Feld({ label, hinweis, children, style }: { label: ReactNode; hinweis?: ReactNode; children: ReactNode; style?: React.CSSProperties }) {
  return (
    <label className="feld" style={style}>
      <span>{label}</span>
      {children}
      {hinweis && <small>{hinweis}</small>}
    </label>
  );
}

export function Suchfeld({ wert, setzen, platzhalter = t('Suchen'), autoFocus }: { wert: string; setzen: (v: string) => void; platzhalter?: string; autoFocus?: boolean }) {
  return (
    <div className="suchfeld">
      <Search />
      <input type="search" value={wert} onChange={(e) => setzen(e.target.value)} placeholder={platzhalter} autoFocus={autoFocus} aria-label={platzhalter} />
    </div>
  );
}

export function Segment<T extends string>({ optionen, wert, setzen }: { optionen: { wert: T; name: string }[]; wert: T; setzen: (v: T) => void }) {
  return (
    <div className="segment" role="group">
      {optionen.map((o) => (
        <button key={o.wert} type="button" aria-pressed={o.wert === wert} onClick={() => setzen(o.wert)}>{o.name}</button>
      ))}
    </div>
  );
}

export function Reiter<T extends string>({ liste, aktiv, setzen }: { liste: { id: T; name: string; zaehler?: number | null; icon?: ReactNode }[]; aktiv: T; setzen: (v: T) => void }) {
  return (
    <div className="reiter" role="tablist">
      {liste.map((r) => (
        <button key={r.id} type="button" role="tab" aria-selected={r.id === aktiv} onClick={() => setzen(r.id)}>
          {r.icon}
          {r.name}
          {r.zaehler != null && <span className="zaehler">{r.zaehler}</span>}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel (Seitenleiste) und Dialog, Esc schließt (GR-13)
// ---------------------------------------------------------------------------
function useEsc(aktiv: boolean, fn: () => void) {
  useEffect(() => {
    if (!aktiv) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') fn(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [aktiv, fn]);
}

export function Panel({ offen = true, titel, untertitel, breit, schliessen, fuss, children }: { offen?: boolean; titel: ReactNode; untertitel?: ReactNode; breit?: boolean; schliessen: () => void; fuss?: ReactNode; children: ReactNode }) {
  useEsc(offen, schliessen);
  if (!offen) return null;
  return (
    <>
      <div className="schleier" onClick={schliessen} />
      <aside className={`panel${breit ? ' panel-breit' : ''}`} role="dialog" aria-modal="true">
        <div className="panel-kopf">
          <div style={{ minWidth: 0 }}>
            <h2>{titel}</h2>
            {untertitel && <div className="untertitel">{untertitel}</div>}
          </div>
          <Knopf art="leise" icon={<X />} onClick={schliessen} aria-label={t('Schließen')} />
        </div>
        <div className="panel-inhalt">{children}</div>
        {fuss && <div className="panel-fuss">{fuss}</div>}
      </aside>
    </>
  );
}

export function Dialog({ offen = true, titel, schliessen, fuss, children }: { offen?: boolean; titel: ReactNode; schliessen: () => void; fuss?: ReactNode; children: ReactNode }) {
  useEsc(offen, schliessen);
  if (!offen) return null;
  return (
    <>
      <div className="schleier" onClick={schliessen} />
      <div className="dialog" role="dialog" aria-modal="true">
        <div className="dialog-inhalt">
          <h2>{titel}</h2>
          {children}
        </div>
        {fuss && <div className="dialog-fuss">{fuss}</div>}
      </div>
    </>
  );
}

// Bestätigung als Promise: const ok = await bestaetigen({ titel, text, ja: 'Löschen', gefahr: true })
interface BestaetigenOpt { titel: string; text?: ReactNode; ja?: string; gefahr?: boolean }
const BestaetigenCtx = createContext<(o: BestaetigenOpt) => Promise<boolean>>(async () => false);
export const useBestaetigen = () => useContext(BestaetigenCtx);

// ---------------------------------------------------------------------------
// Meldungen (Toasts)
// ---------------------------------------------------------------------------
interface Meldung { id: number; text: string; fehler: boolean; geht?: boolean }
const MeldungCtx = createContext<(text: string, fehler?: boolean) => void>(() => {});
export const useMeldung = () => useContext(MeldungCtx);

/** Toast mit Standzeit, die nur bei sichtbarem Tab läuft (DS-11). Fehler bleiben länger stehen. */
function MeldungEintrag({ m, entfernen }: { m: Meldung; entfernen: (id: number) => void }) {
  useEffect(() => {
    let rest = m.fehler ? 7000 : 4000;
    let start = performance.now();
    let zeit: number | null = null;
    const laufen = () => { start = performance.now(); zeit = window.setTimeout(() => entfernen(m.id), rest); };
    const pause = () => { if (zeit != null) { window.clearTimeout(zeit); zeit = null; rest -= performance.now() - start; } };
    const sicht = () => (document.hidden ? pause() : laufen());
    if (!document.hidden) laufen();
    document.addEventListener('visibilitychange', sicht);
    return () => { pause(); document.removeEventListener('visibilitychange', sicht); };
  }, [m.id, m.fehler, entfernen]);
  return (
    <div className={`meldung${m.fehler ? ' meldung-fehler' : ''}`} data-geht={m.geht ? 'true' : undefined} role={m.fehler ? 'alert' : undefined}>
      {m.fehler ? <XCircle /> : <CheckCircle2 />}
      <span>{m.text}</span>
    </div>
  );
}

export function OberflaecheProvider({ children }: { children: ReactNode }) {
  const [meldungen, setMeldungen] = useState<Meldung[]>([]);
  const zaehler = useRef(0);
  // Austritt 200 ms: erst als gehend markieren, dann entfernen
  const entfernen = useCallback((id: number) => {
    setMeldungen((m) => m.map((x) => (x.id === id ? { ...x, geht: true } : x)));
    window.setTimeout(() => setMeldungen((m) => m.filter((x) => x.id !== id)), 200);
  }, []);
  const melden = useCallback((text: string, fehler = false) => {
    const id = ++zaehler.current;
    // höchstens drei gleichzeitig
    setMeldungen((m) => [...m.filter((x) => !x.geht).slice(-2), { id, text, fehler }]);
  }, []);

  const [frage, setFrage] = useState<(BestaetigenOpt & { antwort: (b: boolean) => void }) | null>(null);
  const bestaetigen = useCallback((o: BestaetigenOpt) => new Promise<boolean>((resolve) => setFrage({ ...o, antwort: resolve })), []);
  const beantworten = (b: boolean) => { frage?.antwort(b); setFrage(null); };

  return (
    <MeldungCtx.Provider value={melden}>
      <BestaetigenCtx.Provider value={bestaetigen}>
        {children}
        <div className="meldungen" aria-live="polite">
          {meldungen.map((m) => <MeldungEintrag key={m.id} m={m} entfernen={entfernen} />)}
        </div>
        {frage && (
          <Dialog titel={frage.titel} schliessen={() => beantworten(false)} fuss={<>
            <Knopf onClick={() => beantworten(false)}>{t('Abbrechen')}</Knopf>
            <Knopf art={frage.gefahr ? 'gefahr' : 'primaer'} onClick={() => beantworten(true)} autoFocus>{frage.ja ?? t('Bestätigen')}</Knopf>
          </>}>
            {frage.text && <div className="text-2">{frage.text}</div>}
          </Dialog>
        )}
      </BestaetigenCtx.Provider>
    </MeldungCtx.Provider>
  );
}

// ---------------------------------------------------------------------------
// Fehler in Klartext (GR-11)
// ---------------------------------------------------------------------------
export function fehlertext(e: unknown): string {
  const m = typeof e === 'string' ? e : (e as { message?: string })?.message ?? '';
  const code = (e as { code?: string })?.code ?? '';
  if (code === '23505' || /duplicate key/i.test(m)) return t('Dieser Eintrag existiert bereits (Nummer oder Name doppelt).');
  if (code === '23503' || /foreign key/i.test(m)) return t('Der Eintrag wird noch an anderer Stelle verwendet oder verweist auf etwas, das fehlt.');
  if (code === '42501' || /row-level security|permission denied/i.test(m)) return t('Dafür fehlt dir die Berechtigung.');
  if (code === '23514' || /check constraint/i.test(m)) {
    if (/imo/i.test(m)) return t('Die IMO-Nummer ist ungültig (Prüfziffer stimmt nicht).');
    return t('Eine Eingabe ist ungültig. Bitte Pflichtfelder und Formate prüfen.');
  }
  if (code === '23502' || /not-null/i.test(m)) return t('Ein Pflichtfeld fehlt.');
  if (/JWT|session/i.test(m)) return t('Die Sitzung ist abgelaufen. Bitte neu anmelden.');
  if (/Failed to fetch|NetworkError/i.test(m)) return t('Keine Verbindung zum Server. Bitte Internetverbindung prüfen.');
  return m || t('Unbekannter Fehler.');
}

// ---------------------------------------------------------------------------
// Daten laden mit Zustand
// ---------------------------------------------------------------------------
export function useLaden<T>(fn: () => Promise<T>, deps: DependencyList) {
  const [daten, setDaten] = useState<T | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const lauf = useRef(0);
  const neu = useCallback(async () => {
    const nr = ++lauf.current;
    setLaden(true);
    try {
      const d = await fn();
      if (nr === lauf.current) { setDaten(d); setFehler(null); }
    } catch (e) {
      if (nr === lauf.current) setFehler(fehlertext(e));
    } finally {
      if (nr === lauf.current) setLaden(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { void neu(); }, [neu]);
  return { daten, laden, fehler, neu, setDaten };
}

/** Wirft den Supabase-Fehler, damit useLaden ihn als Klartext zeigt */
export function pruefe<T>(r: { data: T | null; error: unknown }): T {
  if (r.error) throw r.error;
  return r.data as T;
}

// ---------------------------------------------------------------------------
// KI-Kontingent als Ladebalken (DB-06)
// ---------------------------------------------------------------------------
export function KontingentBalken({ verbraucht, kontingent }: { verbraucht: number; kontingent: number }) {
  if (!kontingent) return <div className="klein leise">{t('Kein Monatskontingent festgelegt')}</div>;
  const anteil = Math.min(1, verbraucht / kontingent);
  const rest = 1 - anteil;
  const art = rest <= 0.2 ? 'fehler' : rest <= 0.3 ? 'warn' : '';
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div className={`balken ${art}`} role="progressbar" aria-valuenow={Math.round(anteil * 100)} aria-valuemin={0} aria-valuemax={100}>
        <span style={{ transform: `scaleX(${anteil})` }} />
      </div>
      <div className="klein leise" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>{t('{n} % verbraucht', { n: Math.round(anteil * 100) })}</span>
        <span>{t('{n} % frei', { n: Math.round(rest * 100) })}</span>
      </div>
    </div>
  );
}

/** Einfache Markdown-Darstellung (Absätze, Listen, Fett, Links, Code) für Assistent und Wiki */
export function Markdown({ text, linkKlick }: { text: string; linkKlick?: (href: string) => void }) {
  const zeilen = text.replace(/\r/g, '').split('\n');
  const bloecke: ReactNode[] = [];
  let liste: { geordnet: boolean; punkte: string[] } | null = null;
  const inline = (s: string, k: string): ReactNode[] => {
    const teile: ReactNode[] = [];
    const re = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\)|`[^`]+`)/g;
    let letzte = 0; let m: RegExpExecArray | null; let i = 0;
    while ((m = re.exec(s))) {
      if (m.index > letzte) teile.push(s.slice(letzte, m.index));
      const t = m[0];
      if (t.startsWith('**')) teile.push(<strong key={k + i++}>{t.slice(2, -2)}</strong>);
      else if (t.startsWith('`')) teile.push(<code key={k + i++}>{t.slice(1, -1)}</code>);
      else {
        const mm = t.match(/\[([^\]]+)\]\(([^)]+)\)/)!;
        const href = mm[2];
        teile.push(href.startsWith('/')
          ? <Link key={k + i++} to={href} onClick={() => linkKlick?.(href)}>{mm[1]}</Link>
          : <a key={k + i++} href={href} target="_blank" rel="noreferrer">{mm[1]}</a>);
      }
      letzte = m.index + t.length;
    }
    if (letzte < s.length) teile.push(s.slice(letzte));
    return teile;
  };
  const listeAbschliessen = () => {
    if (!liste) return;
    const L = liste.geordnet ? 'ol' : 'ul';
    bloecke.push(<L key={'l' + bloecke.length}>{liste.punkte.map((p, i) => <li key={i}>{inline(p, `l${bloecke.length}-${i}-`)}</li>)}</L>);
    liste = null;
  };
  zeilen.forEach((z, idx) => {
    const ul = z.match(/^\s*[-*•]\s+(.*)$/);
    const ol = z.match(/^\s*\d+[.)]\s+(.*)$/);
    if (ul || ol) {
      const geordnet = Boolean(ol);
      if (!liste || liste.geordnet !== geordnet) { listeAbschliessen(); liste = { geordnet, punkte: [] }; }
      liste.punkte.push((ul ?? ol)![1]);
      return;
    }
    listeAbschliessen();
    const h = z.match(/^(#{1,3})\s+(.*)$/);
    if (h) { bloecke.push(<h3 key={'h' + idx}>{inline(h[2], 'h' + idx)}</h3>); return; }
    if (z.trim()) bloecke.push(<p key={'p' + idx}>{inline(z, 'p' + idx)}</p>);
  });
  listeAbschliessen();
  return <div className="prosa">{bloecke}</div>;
}
export { ZahlFeld } from './zahl-feld';
